// src/admin/pages/ResetPasswordModal.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import AdminModal from '../components/AdminModal.jsx';
import { resetUserPassword } from '../adminApi.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color: COLORS.ink, fontSize: '14px', boxSizing: 'border-box',
  outline: 'none', fontFamily: FONT_SANS,
};

export default function ResetPasswordModal({ open, onClose, userId }) {
  const { t } = useTranslation('admin');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetUserPassword(userId, password);
      setSuccess(true);
      setPassword('');
      setTimeout(() => { setSuccess(false); onClose(); }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setPassword('');
    setError('');
    setSuccess(false);
    onClose();
  }

  return (
    <AdminModal open={open} onClose={handleClose} title={t('resetPassword.title')} width={400}>
      {success ? (
        <div style={{ padding: '16px', textAlign: 'center', color: SEMANTIC.verde.fg, fontSize: '14px' }}>
          {t('shared.passwordUpdated')}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px', fontWeight: 500 }}>
              {t('resetPassword.newPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                style={{ ...inputStyle, paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: COLORS.textSecondary }}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <span style={{ fontSize: '11px', color: COLORS.textSecondary, marginTop: '4px', display: 'block' }}>{t('shared.minChars')}</span>
          </div>
          {error && (
            <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.bg}`, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '10px', borderRadius: RADII.controlInner, border: 'none',
              background: loading ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
              fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('resetPassword.updating') : t('resetPassword.title')}
          </button>
        </form>
      )}
    </AdminModal>
  );
}
